// Das Kolonie-Panel (Akt III) — die Verdrahtung um `ui/colony.ts`.
//
// Hier stehen nur Knöpfe, Modi und laufende Zahlen; Texte und Schlüssel kommen
// als reine Funktionen aus `ui/colony.ts`. Die Planetenszene kennt dieses
// Panel nicht (FG-04): sie meldet einen Tipp an die Verdrahtung, und die
// reicht ihn hierher (`tapTile`).
//
// Drei Regeln aus `oberflaeche.md` tragen das Panel:
//
// * **DOM-Referenzen einmal auflösen**, im Konstruktor — ein fehlendes Element
//   ist ein lauter Fehler.
// * **Neubau nur bei echter Änderung** — die Schlüssel bilden ab, was die
//   Listen zeigen. Die Kolonie produziert ununterbrochen; ein Schlüssel mit
//   Lagerzahlen zöge dem Spieler den Ausbau-Knopf unter dem Finger weg.
// * **Laufende Zahlen an ihren Platz**, mit Knoten, die beim Neubau
//   eingesammelt werden — nicht je Bild gesucht.

import { fmt } from '../core/format';
import { balancing, planets } from '../data/balancing';
import { drawIcon } from '../gfx/icons';
import {
  founded, rates, shownGoods, buildingAt, type ColonyView, type Flow,
} from '../sim/colony';
import { store } from '../state/store';
import {
  COLONY_ATTR, buildMenuHtml, buildMenuKey, goodsHtml, goodsKey, planetCardHtml, planetCardKey,
  planetTabsHtml, planetTabsKey, powerText, projectHtml, projectKey, rateStr, selectionHtml,
  selectionKey, shownGoodIds,
} from './colony';

const el = (id: string): HTMLElement => {
  const found = document.getElementById(id);
  if (!found) throw new Error(`Kolonie-Element fehlt: ${id}`);
  return found;
};

/** Was das Panel auslöst. Die Verdrahtung macht daraus Züge (`net/moves.ts`). */
export interface ColonyActions {
  place(planet: string, kind: string, x: number, y: number): void;
  upgrade(planet: string, x: number, y: number, times: number): void;
  recipe(planet: string, x: number, y: number, recipe: string): void;
  switchOn(planet: string, x: number, y: number, on: boolean): void;
  relocate(planet: string, x: number, y: number, tx: number, ty: number): void;
  project(planet: string): void;
  /** Den Landedialog öffnen — mit Preis, bevor gezahlt wird. */
  land(planet: string): void;
  /** Kurs auf den Planeten — die Weltszene setzt ihn. */
  course(planet: string): void;
}

/** Wie oft die Raten neu gerechnet werden — sie sind eine Anzeige, kein Takt. */
const RATEN_SEKUNDEN = 0.5;

export class ColonyPanel {
  private readonly tabs = el('colony-tabs');
  private readonly card = el('colony-card');
  private readonly power = el('colony-power');
  private readonly select = el('colony-select');
  private readonly menu = el('colony-build');
  private readonly projectBox = el('colony-project');
  private readonly goodsBox = el('colony-goods');
  private readonly side = el('colony-side');

  /** Oberflächenzustand — kein Fortschritt, kein Spielstand (FG-28). */
  view: ColonyView = { planet: planets()[0]?.id ?? '', selected: null, placing: '', moving: false };

  private tabsKey = '';
  private cardKey = '';
  private selectKey = '';
  private menuKey = '';
  private projectKeyNow = '';
  private goodsKeyNow = '';

  private flow: Flow | null = null;
  private flowStamp = -1;
  private readonly shown: Record<string, number> = {};
  private goodsLive: Array<{ id: string; num: HTMLElement; rate: HTMLElement }> = [];
  private barsLive: Array<{ id: string; need: number; bar: HTMLElement; num: HTMLElement | null }> = [];

  constructor(private readonly actions: ColonyActions) {}

  /** Den Reiter auf einen Planeten stellen — etwa nach der Landung. */
  focus(planetId: string): void {
    if (this.view.planet === planetId) return;
    this.view = { planet: planetId, selected: null, placing: '', moving: false };
  }

  /** `Esc`: erst den Bauplan weglegen, dann das Versetzen abbrechen. Gibt zurück, ob etwas lief. */
  cancel(): boolean {
    if (this.view.placing || this.view.moving) {
      this.view = { ...this.view, placing: '', moving: false };
      return true;
    }
    return false;
  }

  /** Die Raten der Kolonie — zwischengespeichert, höchstens zweimal je Sekunde gerechnet. */
  currentFlow(now: number): Flow {
    if (!this.flow || now - this.flowStamp >= RATEN_SEKUNDEN || now < this.flowStamp) {
      this.flow = rates(store.save.colony);
      this.flowStamp = now;
    }
    return this.flow;
  }

  /**
   * Ein Tipp auf eine Kachel der Planetenszene.
   *
   * **Erster Tipp wählt, zweiter handelt** — wo ein Tooltip die Entscheidung
   * trägt, gilt die Regel des Technologiebaums. Nur mit Bauplan in der Hand
   * baut der Tipp sofort: dann ist die Entscheidung schon gefallen.
   */
  tapTile(x: number, y: number): void {
    const v = this.view;
    if (v.placing) {
      this.actions.place(v.planet, v.placing, x, y);
      this.view = { ...v, selected: { x, y } };
      return;
    }
    if (v.moving && v.selected) {
      this.actions.relocate(v.planet, v.selected.x, v.selected.y, x, y);
      this.view = { ...v, moving: false, selected: { x, y } };
      return;
    }
    this.view = { ...v, selected: { x, y } };
  }

  /** Einmal je Bild, solange der Reiter offen ist. */
  render(now: number): void {
    const flow = this.currentFlow(now);
    this.renderTabs();
    const planet = planets().find((entry) => entry.id === this.view.planet);
    const besiedelt = Boolean(store.save.colony.planets[this.view.planet]);
    this.card.classList.toggle('hidden', besiedelt);
    this.side.classList.toggle('cnone', !besiedelt);
    if (!besiedelt) {
      if (planet) this.renderCard();
      return;
    }
    this.renderSelect(flow);
    this.renderMenu();
    this.renderProject();
    this.renderGoods(flow);
    this.refreshLive(flow);
  }

  private renderTabs(): void {
    const key = planetTabsKey(store.save, this.view.planet);
    if (key === this.tabsKey) return;
    this.tabsKey = key;
    this.tabs.innerHTML = planetTabsHtml(store.save, this.view.planet);
    for (const button of this.tabs.querySelectorAll<HTMLButtonElement>(`[${COLONY_ATTR.planet}]`)) {
      button.onclick = () => {
        this.focus(button.getAttribute(COLONY_ATTR.planet) ?? '');
        this.resetKeys();
      };
    }
  }

  private renderCard(): void {
    const planet = planets().find((entry) => entry.id === this.view.planet);
    if (!planet) return;
    const key = planetCardKey(store.save, planet);
    if (key === this.cardKey) return;
    this.cardKey = key;
    this.card.innerHTML = planetCardHtml(store.save, planet);
    paintIcons(this.card);
    this.card.querySelector<HTMLButtonElement>(`[${COLONY_ATTR.land}]`)
      ?.addEventListener('click', () => this.actions.land(planet.id));
    this.card.querySelector<HTMLButtonElement>(`[${COLONY_ATTR.course}]`)
      ?.addEventListener('click', () => this.actions.course(planet.id));
  }

  private renderSelect(flow: Flow): void {
    const key = selectionKey(store.save, this.view, flow);
    if (key === this.selectKey) return;
    this.selectKey = key;
    this.select.innerHTML = selectionHtml(store.save, this.view, flow);
    const v = this.view;
    const at = v.selected;
    const knopf = (attr: string): HTMLButtonElement[] =>
      [...this.select.querySelectorAll<HTMLButtonElement>(`[${attr}]`)];
    for (const button of knopf(COLONY_ATTR.cancel)) button.onclick = () => this.cancel();
    if (!at) return;
    for (const button of knopf(COLONY_ATTR.upgrade)) {
      const wert = button.getAttribute(COLONY_ATTR.upgrade) ?? '1';
      const mal = wert === 'max' ? balancing().colony.maxUpgradeRuns : Number(wert);
      button.onclick = () => this.actions.upgrade(v.planet, at.x, at.y, mal);
    }
    for (const button of knopf(COLONY_ATTR.recipe)) {
      button.onclick = () => this.actions.recipe(v.planet, at.x, at.y, button.getAttribute(COLONY_ATTR.recipe) ?? '');
    }
    for (const button of knopf(COLONY_ATTR.switch)) {
      button.onclick = () => this.actions.switchOn(v.planet, at.x, at.y, button.getAttribute(COLONY_ATTR.switch) === 'on');
    }
    for (const button of knopf(COLONY_ATTR.move)) {
      button.onclick = () => { this.view = { ...this.view, moving: true }; };
    }
  }

  private renderMenu(): void {
    const key = buildMenuKey(store.save, this.view);
    if (key === this.menuKey) return;
    this.menuKey = key;
    this.menu.innerHTML = buildMenuHtml(store.save, this.view);
    paintIcons(this.menu);
    for (const button of this.menu.querySelectorAll<HTMLButtonElement>(`[${COLONY_ATTR.place}]`)) {
      button.onclick = () => {
        const art = button.getAttribute(COLONY_ATTR.place) ?? '';
        this.view = { ...this.view, placing: this.view.placing === art ? '' : art, moving: false };
      };
    }
  }

  private renderProject(): void {
    const key = projectKey(store.save, this.view.planet);
    if (key === this.projectKeyNow) return;
    this.projectKeyNow = key;
    this.projectBox.innerHTML = projectHtml(store.save, this.view.planet);
    this.projectBox.querySelector<HTMLButtonElement>(`[${COLONY_ATTR.project}]`)
      ?.addEventListener('click', () => this.actions.project(this.view.planet));
    this.barsLive = [...this.projectBox.querySelectorAll<HTMLElement>('[data-cbar]')].map((bar) => ({
      id: bar.dataset.cbar ?? '',
      need: Number(bar.dataset.need ?? '1'),
      bar,
      num: this.projectBox.querySelector<HTMLElement>(`[data-cnum="${bar.dataset.cbar ?? ''}"]`),
    }));
  }

  private renderGoods(flow: Flow): void {
    const ids = shownGoodIds(store.save.colony, flow);
    const key = goodsKey(ids);
    if (key === this.goodsKeyNow) return;
    this.goodsKeyNow = key;
    this.goodsBox.innerHTML = goodsHtml(ids);
    paintIcons(this.goodsBox, 18);
    this.goodsLive = ids.map((id) => ({
      id,
      num: this.goodsBox.querySelector<HTMLElement>(`[data-cgood="${id}"]`) as HTMLElement,
      rate: this.goodsBox.querySelector<HTMLElement>(`[data-crate="${id}"]`) as HTMLElement,
    }));
  }

  /** Die laufenden Zahlen — je Bild an ihren Platz, ohne Neubau. */
  private refreshLive(flow: Flow): void {
    shownGoods(store.save.colony, flow, store.colonyElapsed(), this.shown);
    for (const zeile of this.goodsLive) {
      zeile.num.textContent = fmt(this.shown[zeile.id] ?? 0);
      zeile.rate.textContent = rateStr((flow.made[zeile.id] ?? 0) - (flow.used[zeile.id] ?? 0)) + '/s';
    }
    for (const balken of this.barsLive) {
      const hat = this.shown[balken.id] ?? store.save.res[balken.id] ?? 0;
      balken.bar.style.width = `${Math.min(100, (hat / Math.max(1, balken.need)) * 100)}%`;
      if (balken.num) balken.num.textContent = `${fmt(Math.min(hat, balken.need))} / ${fmt(balken.need)}`;
    }
    this.power.textContent = powerText(flow);
    this.power.classList.toggle('clock', flow.ratio < 0.999);
  }

  /** Nach einem Reiterwechsel alles neu — die Schlüssel kennen den Planeten. */
  resetKeys(): void {
    this.tabsKey = ''; this.cardKey = ''; this.selectKey = ''; this.menuKey = '';
    this.projectKeyNow = ''; this.goodsKeyNow = '';
    this.flow = null;
  }

  /** Gibt es überhaupt eine Kolonie? Der Reiter zeigt sich mit dem Heimatfeuer. */
  static visible(): boolean {
    return founded(store.save.colony)
      || balancing().builds.findIndex((entry) => entry.id === 'homefire') < store.save.built;
  }

  /** Die Anlage auf der ausgewählten Kachel — für die Verdrahtung, etwa nach einem Zug. */
  selectedBuilding(): ReturnType<typeof buildingAt> {
    const outpost = store.save.colony.planets[this.view.planet];
    const at = this.view.selected;
    return outpost && at ? buildingAt(outpost, at.x, at.y) : undefined;
  }
}

/** Icons in ein Bild malen — dieselbe Bauweise wie im übrigen Panel. */
function paintIcons(root: HTMLElement, size = 34): void {
  root.querySelectorAll<HTMLImageElement>('img[data-icon]').forEach((img) => {
    const canvas = document.createElement('canvas');
    canvas.width = size; canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    drawIcon(ctx, img.dataset.icon ?? 'lock', 1, 1, size - 2);
    img.src = canvas.toDataURL();
  });
}
